import { IonContent, IonNote, IonPage, useIonToast } from "@ionic/react";
import React, { useCallback, useContext } from "react";
import PageHeader from "../../components/PageHeader";
import { getUserSetupValues, saveUserValue } from "../../api/UserSetups";
import { useOfflineStore } from "../../store/offline-mode";
import { useNetworkStatusStore } from "../../store/network-status";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../../util/Translations";
import "./Settings.css";

export const SETTINGS_ROOT = "/Settings/settings";

// sy_user_setup section/parameters for the default queries per module
export const QUERY_SECTIONS = {
  workOrder: "QW--Work Order",
  equipment: "QW--Equipment",
  inventory: "QW--Item",
  requisition: "QW--Requisition",
  cycleCount: "QW--Cycle Count",
  employee: "QW--Resource",
};
const STARTUP_QUERY = "startup query";

// sy_user_setup keys for messaging (shared with the EAM web notification settings)
export const MESSAGING_SECTION = "Messaging";
export const MESSAGING_ENABLED = "messaging_enabled";
export const NOTIFY_TYPES = [
  {
    parameter: "mobile_notify_wo_review",
    labelKey: "lbl_mob_notify_wo_review",
    label: "Work order reviews",
  },
  {
    parameter: "mobile_notify_rq_review",
    labelKey: "lbl_mob_notify_rq_review",
    label: "Requisition approvals",
  },
  {
    parameter: "mobile_notify_po_review",
    labelKey: "lbl_mob_notify_po_review",
    label: "Purchase order approvals",
  },
];

/**
 * EAM-backed settings are read-only when the app is in offline mode or the
 * device has no connection; device-local settings stay editable.
 */
export const useEamSettingsReadOnly = () => {
  const isOffline = useOfflineStore((state) => state.isOffline);
  const isOnline = useNetworkStatusStore((state) => state.isOnline);
  return isOffline || !isOnline;
};

/** Saves one sy_user_setup value; resolves false when the save failed. */
export const saveEamSetting = async (
  section: string,
  parameter: string,
  value: string | number
) => {
  try {
    const response = await saveUserValue({
      UserSection: section,
      UserParameter: parameter,
      UserValue: value,
    });
    return !response?.isError;
  } catch {
    return false;
  }
};

/** Toast shown when an EAM-backed setting could not be saved and was reverted. */
export const useSaveErrorToast = () => {
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [presentToast] = useIonToast();
  return useCallback(() => {
    presentToast({
      message:
        translatedMessages["SettingSaveFailed"]?.MessageText ||
        "Couldn't save this setting. Please try again.",
      duration: 2000,
      position: "top",
      color: "danger",
    });
  }, [presentToast, translatedMessages]);
};

/** Reads the default query name saved for a module section ("" when not set). */
export const readDefaultQuery = async (section: string) => {
  const response = await getUserSetupValues({
    Section: section,
    ParameterList: [STARTUP_QUERY],
  });
  const value: string | undefined = response?.data?.[STARTUP_QUERY];
  if (!value) return "";
  return value.includes("--") ? value.split("--")[1] : value;
};

export interface MessagingSettings {
  enabled: boolean;
  types: Record<string, boolean>;
}

/** Messaging is on only for "1"; a type is on unless explicitly saved as "0". */
export const readMessagingSettings = async (): Promise<MessagingSettings> => {
  const response = await getUserSetupValues({
    Section: MESSAGING_SECTION,
    ParameterList: [MESSAGING_ENABLED, ...NOTIFY_TYPES.map((t) => t.parameter)],
  });
  if (response?.isError) throw new Error("Unable to read messaging settings");
  const data = response?.data ?? {};
  const types: Record<string, boolean> = {};
  NOTIFY_TYPES.forEach((t) => {
    types[t.parameter] = data[t.parameter] !== "0";
  });
  return { enabled: data[MESSAGING_ENABLED] === "1", types };
};

export const ReadOnlyNote: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  return (
    <IonNote className="settings-readonly-note">
      {translations["lbl_mob_settings_connect_to_change"] ||
        "Connect to change this setting."}
    </IonNote>
  );
};

/** Shell for a settings sub-page: back header and the centred content column. */
export const SettingsSubPage: React.FC<{
  title: string;
  backHref?: string;
  children?: React.ReactNode;
}> = ({ title, backHref = SETTINGS_ROOT, children }) => (
  <IonPage>
    <PageHeader title={title} showBack defaultBackHref={backHref} />
    <IonContent fullscreen className="settings-content">
      <div className="settings-container">{children}</div>
    </IonContent>
  </IonPage>
);
